import axios, { AxiosInstance, AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { getWorkingPlantId, takesPlantFilter } from '../lib/plantScope';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // The plant chosen in the header narrows the sales, purchase and work order lists
        const plantId = getWorkingPlantId();
        if (plantId && takesPlantFilter(config.method, config.url) && config.params?.plantId === undefined) {
          config.params = { ...config.params, plantId };
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              // TODO: Implement refresh token logic
              // const response = await this.refreshAccessToken(refreshToken);
              // localStorage.setItem('accessToken', response.data.accessToken);
              // return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Redirect to login
            localStorage.clear();
            window.location.href = '/login';
          }

          // The account was suspended, deleted or its sign-in ran out: back to the sign-in page.
          // A wrong password on the sign-in page itself is also a 401 and stays there.
          const isAuthCall = String(originalRequest?.url || '').includes('/auth/');
          if (!isAuthCall && localStorage.getItem('accessToken') && window.location.pathname !== '/login') {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }

        if (error.response?.status === 403) {
          const reason = (error.response.data as { message?: unknown } | undefined)?.message;
          toast.error(typeof reason === 'string' && reason ? reason : 'You do not have permission to perform this action', { id: 'forbidden' });
          // The role may have changed since the page loaded
          window.dispatchEvent(new Event('auth:forbidden'));
        } else if (error.response?.status === 404) {
          toast.error('Resource not found');
        } else if (error.response?.status === 500) {
          toast.error('Server error. Please try again later');
        }

        return Promise.reject(error);
      }
    );
  }

  get instance() {
    return this.api;
  }
}

export const apiService = new ApiService();
export const api = apiService.instance;
export default api;